import * as React from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { ACalculatedFields, CalculatedField, CalculatedFieldTarget, FieldDistribution, getFieldDistribution } from '../../../atoms/calculatedFields';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import Input from '../../../components_v2/input/Input';
import Popup from '../../../components_v2/popup/Popup';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import { DeleteDot } from '../../../styles/global/css/Dot';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { PrimaryColor, BorderColor, DarkGreySidely2, GreySidely4, RedSidely, SidelyBlack } from '../../../styles/global/css/Utils';
import { Translate, translateToString } from '../../../styles/global/translate';
import { launchToastError } from '../../forms/visual-form-editor/visualFormEditor';
import { FlexDiv } from '../../products/style';

/// The rules the editor can write. `bands` is deliberately absent: it needs a
/// nested list and nobody has asked for one yet, so a card carrying one is
/// shown as it is and left alone rather than half-edited into something else.
type RuleKind = 'threshold' | 'inverse' | 'ratio' | 'compliance';
type Rule = { kind: string, floor?: number, target?: number, ceiling?: number, bands?: unknown[] };
type Criterion = { name: string, field: number, weight: number, rule: Rule };
type Scorecard = { criteria: Criterion[], maluses?: unknown[], min_coverage?: number };

/// Where a criterion's field sits across the estate, and the two answers that
/// are not a distribution: still being read, and could not be read.
type Spread = { state: 'loading' } | { state: 'failed' } | { state: 'read', distribution: FieldDistribution };

const EDITABLE: RuleKind[] = ['threshold', 'inverse', 'ratio', 'compliance'];

const Title = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	padding: 18px 20px;
	border-bottom: 1px solid ${BorderColor};
	color: ${SidelyBlack};
	font-size: 15px;
	font-weight: 600;
`;

const Body = styled.div`
	display: flex;
	flex-flow: column;
	gap: 16px;
	padding: 20px;
	overflow-y: auto;
	height: calc(100vh - 75px);
`;

const Section = styled.h5`
	margin: 0;
	font-size: 12px;
	font-weight: 500;
	color: ${GreySidely4};
`;

const CriterionBox = styled.div`
	display: flex;
	flex-flow: column;
	gap: 10px;
	padding: 12px 14px;
	border: 1px solid ${BorderColor};
	border-radius: 6px;
`;

const Worth = styled.span`
	font-size: 12px;
	color: ${DarkGreySidely2};
	white-space: nowrap;
`;

const AddLine = styled.span`
	color: ${PrimaryColor};
	cursor: pointer;
	font-size: 13px;
	width: fit-content;
`;

const Hint = styled.p`
	margin: 0;
	font-size: 11px;
	color: ${GreySidely4};
`;

const Spread = styled.p`
	margin: 0;
	font-size: 11px;
	color: ${GreySidely4};
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	align-items: baseline;
`;

const Warn = styled.span`
	color: ${RedSidely};
`;

const Fit = styled.span`
	color: ${PrimaryColor};
	cursor: pointer;
`;

const numberStyle = { height: '36px', width: '110px', padding: '0 0 0 10px', borderRadius: '5px' };
const nameStyle = { height: '36px', width: '100%', padding: '0 0 0 10px', borderRadius: '5px' };

function defaultRule(kind: RuleKind): Rule {
	switch (kind) {
		case 'threshold': return { kind, floor: 0, target: 100 };
		case 'inverse': return { kind, target: 0, ceiling: 10 };
		case 'ratio': return { kind, target: 100 };
		case 'compliance': return { kind };
	}
}

/// A weight reads as what it is worth, because that is how the card is read:
/// a criterion weighing 3 carries 300 of the points on offer.
function worth(weight: number): string {
	return `${Math.round(weight * 100)}`;
}

function show(value: number | null): string {
	return value === null ? '—' : (Math.round(value * 10) / 10).toString();
}

function parseNumber(raw: string): number | undefined {
	const value = Number(raw.replace(',', '.'));
	return raw.trim() === '' || Number.isNaN(value) ? undefined : value;
}

/// Writes the card a tenant scores on: criteria over the values the workers
/// already store, each turned into points by one rule. It produces the same
/// object the settings page saves for any calculated field, so the server
/// validation is what finally guards it — this only makes a refusal unlikely.
export default function ScorecardEditor(props: {
	isOpen: boolean,
	setIsOpen: (value: boolean) => void,
	setCalculatedField: (value: CalculatedField | null) => void,
	calculatedField: CalculatedField | null,
}): JSX.Element {
	const allFields = useRecoilValue(ACalculatedFields);
	const [name, setName] = React.useState('');
	const [coverage, setCoverage] = React.useState(0);
	const [criteria, setCriteria] = React.useState<Criterion[]>([]);
	const [untouched, setUntouched] = React.useState<Scorecard>({ criteria: [] });
	// one fetch per field the card reads, kept while the editor is open: the
	// thresholds are chosen against this and the same field recurs across cards.
	// The three states are told apart on purpose — a line that only appears once
	// the answer lands reads as the editor behaving at random, and a request
	// that failed looked exactly like a field holding no value at all
	const [spreads, setSpreads] = React.useState<Record<number, Spread>>({});
	// the fields already asked for, so a re-render does not ask again: the
	// effect cannot read `spreads` without capturing a stale one
	const asked = React.useRef(new Set<number>());

	// the fields a criterion can read: the tenant's company-level values and the
	// objective completions, which are company-level values too — never a
	// scorecard itself and never a per-product map
	const options = React.useMemo<DropdownData<number>[]>(
		() => allFields
			.filter(field => typeof field.id === 'number'
				&& (field.target === CalculatedFieldTarget.Company || field.target === CalculatedFieldTarget.Objective))
			.map(field => ({ label: field.field_name, value: field.id })),
		[allFields]
	);

	React.useEffect(() => {
		if (!props.isOpen) return;
		const card = (props.calculatedField?.report ?? { criteria: [] }) as unknown as Scorecard;
		setName(props.calculatedField?.field_name ?? '');
		setCoverage(Math.round((card.min_coverage ?? 0) * 100));
		setCriteria(card.criteria ?? []);
		setUntouched(card);
		// values move between two openings of the editor
		asked.current.clear();
		setSpreads({});
	}, [props.isOpen, props.calculatedField]);

	React.useEffect(() => {
		if (!props.isOpen) return;
		criteria
			.map(criterion => criterion.field)
			.filter(field => field > 0 && !asked.current.has(field))
			.forEach(field => {
				asked.current.add(field);
				setSpreads(current => ({ ...current, [field]: { state: 'loading' } }));
				getFieldDistribution(field)
					.then(distribution => setSpreads(current => ({ ...current, [field]: { state: 'read', distribution } })))
					.catch(error => {
						console.error(error);
						setSpreads(current => ({ ...current, [field]: { state: 'failed' } }));
					});
			});
	}, [props.isOpen, criteria]);

	const total = criteria.reduce((sum, criterion) => sum + (criterion.weight > 0 ? criterion.weight * 100 : 0), 0);
	const update = (index: number, change: Partial<Criterion>) =>
		setCriteria(list => list.map((criterion, i) => i === index ? { ...criterion, ...change } : criterion));

	/// What the chosen field holds across the estate, and the one gesture that
	/// turns it into thresholds. Writing a floor without this is how a card
	/// ends up demanding 70 of an estate whose median is 50.
	function spread(criterion: Criterion, index: number): JSX.Element {
		const found = spreads[criterion.field];
		if (!found) return <></>;
		if (found.state === 'loading') return <Spread><Translate id='scorecard_editor.spread_loading' /></Spread>;
		if (found.state === 'failed') return <Spread><Warn><Translate id='scorecard_editor.spread_failed' /></Warn></Spread>;
		const distribution = found.distribution;
		if (distribution.companies === 0) return <Spread><Warn><Translate id='scorecard_editor.spread_none' /></Warn></Spread>;
		const fits = criterion.rule.kind === 'threshold' || criterion.rule.kind === 'inverse';
		return <Spread>
			<span>{translateToString('scorecard_editor.spread_stores', [['COUNT', distribution.companies.toString()]])}</span>
			<span>min {show(distribution.min)}</span>
			<span>p10 {show(distribution.p10)}</span>
			<span>{translateToString('scorecard_editor.median')} {show(distribution.p50)}</span>
			<span>p90 {show(distribution.p90)}</span>
			<span>max {show(distribution.max)}</span>
			{distribution.unreadable > 0 && <Warn>{translateToString('scorecard_editor.spread_unreadable', [['COUNT', distribution.unreadable.toString()]])}</Warn>}
			{fits && distribution.p10 !== null && distribution.p90 !== null && <Fit onClick={() => update(index, {
				rule: criterion.rule.kind === 'threshold'
					? { kind: 'threshold', floor: distribution.p10 ?? 0, target: distribution.p90 ?? 0 }
					: { kind: 'inverse', target: distribution.p10 ?? 0, ceiling: distribution.p90 ?? 0 },
			})}><Translate id='scorecard_editor.spread_fit' /></Fit>}
		</Spread>;
	}

	function save() {
		const trimmed = name.trim();
		if (!trimmed) return launchToastError(translateToString('scorecard_editor.name_required'));
		if (criteria.length === 0) return launchToastError(translateToString('scorecard_editor.criterion_required'));
		props.setCalculatedField({
			id: props.calculatedField ? props.calculatedField.id : { unsync: Date.now() } as unknown as number,
			target: CalculatedFieldTarget.Score,
			field_name: trimmed,
			display_options: props.calculatedField?.display_options,
			// everything the editor does not write — penalties, and any rule it
			// cannot express — rides through untouched
			report: { ...untouched, criteria, min_coverage: coverage / 100 } as unknown as CalculatedField['report'],
			ishidded: props.calculatedField?.ishidded ?? false,
		});
		props.setIsOpen(false);
	}

	return <Popup isOpen={props.isOpen} hasBackground disableOutClick onClickOut={() => props.setIsOpen(false)}
		popupStyle={{ top: '0', left: '55vw', height: '100vh', width: '45vw', animate: true, boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.06)' }}
		content={<>
			<Title>
				<Translate id={props.calculatedField ? 'scorecard_editor.edit' : 'scorecard_editor.create'} />
				<FlexDiv gap='10px'>
					<DefaultButton buttonStyle={ButtonStyle.White} onClick={() => props.setIsOpen(false)}>
						<Translate id='cancel' />
					</DefaultButton>
					<DefaultButton onClick={save}><Translate id='save' /></DefaultButton>
				</FlexDiv>
			</Title>
			<Body>
				<Section><Translate id='name' /></Section>
				<Input inputStyle={nameStyle} name='scorecard-name' type='text' value={name} onChange={setName} />

				<Section><Translate id='scorecard_editor.criteria' /></Section>
				{criteria.map((criterion, index) => {
					const editable = EDITABLE.includes(criterion.rule.kind as RuleKind);
					return <CriterionBox key={`criterion[${index}]`}>
						<FlexDiv justify='space-between' gap='10px'>
							<Input inputStyle={nameStyle} name={`criterion-name[${index}]`} type='text' value={criterion.name}
								onChange={value => update(index, { name: value })} />
							<DeleteDot size='18px' onClick={() => setCriteria(list => list.filter((_, i) => i !== index))} />
						</FlexDiv>
						<FlexDiv gap='10px' flexWrap='wrap' align='center'>
							<Dropdown<number>
								dropdownStyle={{ containerHeight: '36px', optionWidth: '220px', containerWidth: '220px' }}
								name={`criterion-field[${index}]`}
								datalist={options}
								selectedValue={options.find(option => option.value === criterion.field)}
								onChange={value => update(index, { field: value.value ?? criterion.field })}
							/>
							<Dropdown<RuleKind>
								dropdownStyle={{ containerHeight: '36px', optionWidth: '160px', containerWidth: '160px' }}
								name={`criterion-rule[${index}]`}
								datalist={EDITABLE.map(kind => ({ label: translateToString(`scorecard_editor.rule.${kind}`), value: kind }))}
								selectedValue={{ label: translateToString(`scorecard_editor.rule.${criterion.rule.kind}`), value: criterion.rule.kind as RuleKind }}
								onChange={value => value.value && update(index, { rule: defaultRule(value.value) })}
							/>
							<Input inputStyle={numberStyle} name={`criterion-weight[${index}]`} type='number'
								label={translateToString('scorecard_editor.weight')}
								value={criterion.weight?.toString() ?? ''}
								onChange={value => update(index, { weight: parseNumber(value) ?? 0 })} />
							<Worth>{worth(criterion.weight)} <Translate id='score.points' /></Worth>
						</FlexDiv>
						{editable
							? <FlexDiv gap='10px' flexWrap='wrap'>
								{criterion.rule.kind === 'threshold' && <>
									<Input inputStyle={numberStyle} name={`floor[${index}]`} type='number' label={translateToString('scorecard_editor.floor')}
										value={criterion.rule.floor?.toString() ?? ''}
										onChange={value => update(index, { rule: { ...criterion.rule, floor: parseNumber(value) } })} />
									<Input inputStyle={numberStyle} name={`target[${index}]`} type='number' label={translateToString('scorecard_editor.target')}
										value={criterion.rule.target?.toString() ?? ''}
										onChange={value => update(index, { rule: { ...criterion.rule, target: parseNumber(value) } })} />
								</>}
								{criterion.rule.kind === 'inverse' && <>
									<Input inputStyle={numberStyle} name={`target[${index}]`} type='number' label={translateToString('scorecard_editor.target')}
										value={criterion.rule.target?.toString() ?? ''}
										onChange={value => update(index, { rule: { ...criterion.rule, target: parseNumber(value) } })} />
									<Input inputStyle={numberStyle} name={`ceiling[${index}]`} type='number' label={translateToString('scorecard_editor.ceiling')}
										value={criterion.rule.ceiling?.toString() ?? ''}
										onChange={value => update(index, { rule: { ...criterion.rule, ceiling: parseNumber(value) } })} />
								</>}
								{criterion.rule.kind === 'ratio' && <Input inputStyle={numberStyle} name={`objective[${index}]`} type='number'
									label={translateToString('scorecard_editor.objective')}
									value={criterion.rule.target?.toString() ?? ''}
									onChange={value => update(index, { rule: { ...criterion.rule, target: parseNumber(value) } })} />}
							</FlexDiv>
							: <Hint><Translate id='scorecard_editor.rule_not_editable' /></Hint>}
						{editable && <Hint>{translateToString(`scorecard_editor.rule.hint_${criterion.rule.kind}`)}</Hint>}
						{spread(criterion, index)}
					</CriterionBox>;
				})}
				<AddLine onClick={() => setCriteria(list => [...list, {
					name: '',
					field: options[0]?.value ?? 0,
					weight: 1,
					rule: defaultRule('threshold'),
				}])}>
					+ <Translate id='scorecard_editor.add_criterion' />
				</AddLine>
				<Hint>{translateToString('scorecard_editor.total_points', [['POINTS', Math.round(total).toString()]])}</Hint>

				<Section><Translate id='scorecard_editor.min_coverage' /></Section>
				<FlexDiv gap='10px' align='center'>
					<Input inputStyle={numberStyle} name='min-coverage' type='number' value={coverage.toString()}
						onChange={value => setCoverage(parseNumber(value) ?? 0)} />
					<Worth>%</Worth>
				</FlexDiv>
				<Hint><Translate id='scorecard_editor.min_coverage_hint' /></Hint>
			</Body>
		</>}
	/>;
}
